// Field metadata + policy for the Focus flow.
//
// IMPORTANT framing: the CHAT is the orchestrator. It decides which questions
// to ask (emits `needs_input` slots with its own question text) and what it can
// derive (emits `proposed` slots whose value now rides on the verdict). Focus
// only REPRESENTS that: a `needs_input` slot → a question screen (headline = the
// chat's own question), a green slot with a value the user hasn't seen → a
// review screen ("Loom set X — keep or change?"). This file holds the labels,
// the question-fallback copy, and the per-field POLICY that lets us skip
// surfacing some fields by default.

import { useCallback } from "react";

import {
  COMPREHENSION_CRITERION_ID,
  DEFAULT_READINESS_SPEC,
  NARRATIVE_TEMPLATES,
  orderedFields,
  selectComprehensionObjection,
  spliceNarrativeClause,
} from "@loom/shared/readiness";

import {
  useT,
  type Locale,
  type MessageKey,
  type Translator,
} from "../i18n/index.js";

import type { TimelineEntry } from "../chat/useWsSession";

// Field surfacing order is DATA — derived from the shared ReadinessSpec, the
// same source the prompt's field-list reads. Adding a field to the spec appears
// here (and in the prompt) with no edit. The per-field UI copy (FIELD_COPY) and
// elicitation policy (FIELD_POLICY) below stay hand-coded — out of v1 spec
// scope; a new field gets a generic fallback (see `fieldCopy`) until copy is
// added, and still needs apply-mapping to actually write.
export const FIELD_ORDER: readonly string[] = orderedFields(
  DEFAULT_READINESS_SPEC,
).map((f) => f.name);

// Fields the gate does NOT require green (e.g. `milestone`) — derived from the
// spec, not hardcoded. An optional field can be left unset without stranding the
// card, so its question offers a "Skip" affordance (see RefineFlow): the user
// can decline it instead of being forced to pick a value they don't want.
export const OPTIONAL_FIELDS: ReadonlySet<string> = new Set(
  DEFAULT_READINESS_SPEC.fields.filter((f) => !f.required).map((f) => f.name),
);
export const isOptionalField = (field: string): boolean =>
  OPTIONAL_FIELDS.has(field);

/**
 * The catalog key for a skip button, chosen by WHAT SKIPPING DOES.
 *
 * The same milestone question reached the user through two different channels on
 * two consecutive sessions — once as a card question ("Skip — leave this unset")
 * and once as a typed ask ("Skip this question") — and read as two different
 * offers. The channel is an implementation detail; the outcome is not. So a skip
 * over an OPTIONAL FIELD says the field is left unset wherever it is rendered,
 * and only a question bound to no field at all (a scoping ask) says "this
 * question", because there is no field to leave unset.
 *
 * A concern outranks both: skipping one is a decision to leave things as they
 * are, and it is the channel's only retraction.
 */
export const skipLabelKey = (opts: {
  readonly field?: string | null;
  readonly concern?: boolean;
}): MessageKey => {
  if (opts.concern === true) return "focus.refine.concern.skip";
  const field = opts.field ?? null;
  return field !== null && isOptionalField(field)
    ? "focus.refine.skipOptional"
    : "focus.refine.ask.skip";
};

/**
 * Per-field surfacing policy. The chat still derives/owns every field; this only
 * controls whether Focus puts a STEP in front of the user for it.
 *   "auto"   — surface as a question (needs_input) or a review (fresh green value)
 *   "skip"   — never surface; trust the chat (use only for fields the chat
 *              reliably auto-derives, or the gate would never reach all-green)
 *   "derive" — the chat OWNS the value: never surface a fresh green for review
 *              (don't bother the user with an agent-decided value), BUT still
 *              surface a QUESTION if it comes back non-green. The safety valve
 *              "skip" lacks — a field the gate requires green (see propose_card's
 *              structural DoD) can't strand the user if grounding ever fails.
 *
 * `story_points` is "skip": the PM directive forbids asking the user to size
 * work — it's machine-estimated (`estimate_story_points`), so surfacing it is
 * pure noise. It stays "skip" even though this file's own rule above bars that
 * for a gate-required field, because story_points is NOT gate-required: the
 * spec marks it `required: false` precisely so hiding it is safe. (The earlier
 * claim here — that the estimator "always returns a value, so it reliably
 * auto-greens" — was wrong. It can return a value the shape gate refuses, and
 * when it did, the pairing of "skip" with `required: true` stalled the flow with
 * no question and no Accept.) `readiness-hidden-fields.test.ts` enforces the
 * pairing so these two files can never drift back apart.
 *
 * `dod_criteria` is "derive": the PM directive has the agent SET the quality bar
 * itself (scaled to the work — e2e/existing checks always pass; backend-critical
 * → more coverage; low-risk frontend → proportionate) and ground it in the
 * repo's real test/CI setup. That's a technical call a business owner shouldn't
 * field, so the happy-path green is hidden. Unlike story_points it's grounded
 * free-hand and CAN fail to green (no-CI repo, a citation that won't verify) —
 * so it stays surfaceable as a question when non-green rather than "skip", which
 * would dead-end the gate (DoD is structurally required green in propose_card).
 */
export type FieldPolicy = "auto" | "skip" | "derive";
export const FIELD_POLICY: Record<string, FieldPolicy> = {
  story_points: "skip",
  dod_criteria: "derive",
};
export const policyFor = (field: string): FieldPolicy =>
  FIELD_POLICY[field] ?? "auto";

export interface FieldCopy {
  /** Short noun label, e.g. "Priority". */
  readonly label: string;
  /** Uppercase kicker above a question, e.g. "Why". */
  readonly kicker: string;
  /** Quiet helper under the chat's question. */
  readonly help: string;
  /** Longer 1–2 sentence explanation, revealed behind the "details" toggle next
   *  to `help`. Says what the field actually is, for users who want more. */
  readonly detail: string;
  /** Clean fallback question, used when the chat didn't author one (a `red` /
   *  `unavailable` slot whose `reason` is internal gate text, not a question). */
  readonly question: string;
}

export const FIELD_COPY: Record<string, FieldCopy> = {
  role: {
    label: "User",
    kicker: "Who",
    help: "Who this is built for — the kind of person, team, or automated system that will actually use the result.",
    detail:
      "This is the “who” of the user story. It names the audience the work serves — like “finance manager” or “the nightly billing job” — and everything else is written from their point of view: the capability is what they can do, the outcome is what they gain.",
    question: "Who is this for?",
  },
  means: {
    label: "Capability",
    kicker: "What",
    help: "The capability, in plain words.",
    detail:
      "The concrete thing the user should be able to do once this ships, stated as an action rather than a solution — e.g. “export a VAT summary as a PDF”, not “add an export button”. It leaves room for Loom to figure out the how.",
    question: "What do they need to be able to do?",
  },
  ends: {
    label: "Outcome",
    kicker: "Why",
    help: "The outcome this unlocks — the reason it's worth building.",
    detail:
      "The payoff that justifies the work: the value the user gains once they have the capability. It's the “so that…” of the story and the test for whether the feature is actually worth doing.",
    question: "Why does it matter?",
  },
  acceptance_criteria: {
    label: "Acceptance criteria",
    kicker: "Done when",
    help: "The checks that must pass for this to count as done.",
    detail:
      "A short list of observable conditions that prove the feature works as intended. Each one should be checkable by using the result — not by reading the code.",
    question: "How will we know it's done?",
  },
  dod_criteria: {
    label: "Definition of Done",
    kicker: "Quality bar",
    help: "Loom sets the quality bar from how your project is built and tested.",
    detail:
      "The standard every issue meets before it ships — your project's existing checks stay green, and Loom adds the right amount of testing for how risky the change is. You're not asked to set it; Loom decides it from your project's setup and shows it for reference.",
    question:
      "What would make this not done — anything beyond the feature working and your usual checks passing?",
  },
  story_points: {
    label: "Story points",
    kicker: "Size",
    help: "Loom estimates this from the code.",
    detail:
      "A rough size estimate Loom derives from the surrounding code, used for planning. You're not asked to set it — it's machine-estimated and shown only for reference.",
    question: "How big is this?",
  },
  milestone: {
    label: "Milestone",
    kicker: "Milestone",
    help: "Where this lands on the roadmap.",
    detail:
      "The release or planning checkpoint this issue is grouped under, so it shows up in the right place on the roadmap and in reporting.",
    question: "Which milestone should this belong to?",
  },
  priority: {
    label: "Priority",
    kicker: "Priority",
    help: "Relative to everything else waiting in the backlog.",
    detail:
      "How urgently this should be picked up compared with the rest of the backlog. It orders the work — it doesn't change what gets built.",
    question: "How urgent is it?",
  },
};

export const fieldCopy = (field: string): FieldCopy =>
  FIELD_COPY[field] ?? {
    label: field.replace(/_/g, " "),
    kicker: "Question",
    help: "",
    detail: "",
    question: `What should the ${field.replace(/_/g, " ")} be?`,
  };

/** Where a green's value came from (`resolvedSource` on the wire). Only
 *  `"user"` is the user's own words; `"code"` and `"meeting"` are reviewed. */
export type ValueSource = "user" | "code" | "meeting" | null;

/** Human label for where a derived value came from. */
export const sourceLabel = (source: ValueSource): string =>
  source === "code"
    ? "from the codebase"
    : source === "user"
      ? "from what you told Loom"
      : source === "meeting"
        ? "from the meeting"
        : "from your request";

// ───────────────────────────────────────────────────────────────────────────
// Locale-aware copy. The pure `fieldCopy` / `sourceLabel` above stay the
// English source of truth (and keep the no-provider unit tests byte-identical);
// the hooks below read the SAME copy out of the shared message catalog so the
// rendered Focus surface follows the operator's UI locale. A known field reads
// its `field.<name>.<slot>` entries (label/kicker/help/question in fields.ts,
// detail in the focus section); an unknown field keeps the humanized fallback.
// ───────────────────────────────────────────────────────────────────────────

/** Build a localized `FieldCopy` from the catalog via an already-resolved `t`. */
export const fieldCopyWith = (t: Translator, field: string): FieldCopy => {
  if (field in FIELD_COPY) {
    const k = (slot: string): MessageKey => `field.${field}.${slot}` as MessageKey;
    return {
      label: t(k("label")),
      kicker: t(k("kicker")),
      help: t(k("help")),
      detail: t(k("detail")),
      question: t(k("question")),
    };
  }
  const humanized = field.replace(/_/g, " ");
  return {
    label: humanized,
    kicker: t("focus.field.fallback.kicker"),
    help: "",
    detail: "",
    question: t("focus.field.fallback.question", { field: humanized }),
  };
};

/** Hook → `(field) => FieldCopy`, localized for the active UI locale. Stable
 *  across renders (depends only on the locale-stable `t`) so it can sit in a
 *  derivation's dependency array without forcing a recompute every render. */
export const useFieldCopy = (): ((field: string) => FieldCopy) => {
  const t = useT();
  return useCallback((field: string) => fieldCopyWith(t, field), [t]);
};

/** Hook → `(source) => string`, the localized evidence-source label. */
export const useSourceLabel = (): ((source: ValueSource) => string) => {
  const t = useT();
  return useCallback(
    (source: ValueSource) =>
      source === "code"
        ? t("field.source.code")
        : source === "user"
          ? t("field.source.user")
          : source === "meeting"
            ? t("field.source.meeting")
            : t("field.source.request"),
    [t],
  );
};

export type ReadinessEntry = Extract<TimelineEntry, { kind: "readiness_card" }>;
export type Verdict = ReadinessEntry["verdicts"][number];

const fieldRank = (field: string): number => {
  const i = (FIELD_ORDER as ReadonlyArray<string>).indexOf(field);
  return i === -1 ? FIELD_ORDER.length : i;
};

/** Verdicts that Focus surfaces (policy !== "skip"), in story order. */
export const surfacedVerdicts = (card: ReadinessEntry): ReadonlyArray<Verdict> =>
  [...card.verdicts]
    .filter((v) => policyFor(v.field) !== "skip")
    .sort((a, b) => fieldRank(a.field) - fieldRank(b.field));

/**
 * The open fields a STALLED grind screen is really waiting on: with Loom idle,
 * every surfaced field that is not green is the user's to answer — except one
 * they skipped. Empty while Loom is working (`idle: false`), which keeps the
 * screen's "re-checking against the code" line for the time it is true.
 */
export const fieldsWaitingOnUser = (
  card: ReadinessEntry,
  opts: { readonly idle: boolean },
): ReadonlyArray<string> =>
  opts.idle
    ? surfacedVerdicts(card)
        .filter((v) => v.verdict !== "green" && v.declined !== true)
        .map((v) => v.field)
    : [];

export interface CardProgress {
  readonly total: number;
  readonly grounded: number;
  /** 0..1 grounded fraction over surfaced fields, for the progress bar. */
  readonly fraction: number;
}

export const cardProgress = (card: ReadinessEntry): CardProgress => {
  const surfaced = surfacedVerdicts(card);
  const total = surfaced.length;
  const grounded = surfaced.filter((v) => v.verdict === "green").length;
  return { total, grounded, fraction: total === 0 ? 0 : grounded / total };
};

// ───────────────────────────────────────────────────────────────────────────
// The comprehension objection (C1) — the Focus reader.
//
// The SELECTION RULE is not defined here. It lives once, in
// `@loom/shared/readiness`'s `selectComprehensionObjection`, and every consumer
// (the card projection, the Stop hook, this surface) reads it from there —
// which criterion, and non-ok rather than `fail`, with the measurements behind
// both written out at the head of that module.
//
// This file owns only the RENDER decision: which of the judge's two sentences
// to put on screen, and when there is nothing worth a note.
//
// Where the verdict comes from: `partitionMethodologyVerdicts` appends the one
// comprehension verdict to the card's coaching hint list, which is already on
// the browser wire as `invest.verdicts` (`criterion` there is a free-form id
// string, not an INVEST-only enum). `ProposeCardOutput` also carries a
// dedicated `comprehension` field — the authoritative seam — but it does not
// reach the browser yet: `apps/web/src/lib/ws.ts` would have to declare it or
// Zod strips it silently. When it does, prefer it here; the two are the same
// object by construction.
//
// Until now Focus rendered NONE of this. The only consumer was
// `chat/render/ReadinessCard.tsx` — a surface the refine flow never puts the
// user on — which is why the objection naming #78's exact defective span was in
// the browser's own state at the moment the user clicked Keep.
// ───────────────────────────────────────────────────────────────────────────

/** Re-exported so this surface's tests and callers name it once. */
export const COMPREHENSION_CRITERION = COMPREHENSION_CRITERION_ID;

export interface FocusComprehensionObjection {
  /** The rubric id that produced it — for diagnostics, NEVER for copy. */
  readonly criterion: string;
  readonly level: "ok" | "warn" | "fail";
  /**
   * The judge's own words, verbatim. NEVER templated from `criterion`: the
   * criterion's LABEL BLEEDS — it has been measured firing on a story whose
   * means/ends direction is fine, with reasoning that argues a different
   * criterion entirely. Copy written off the id would therefore assert a defect
   * the model did not find. The surrounding UI frame says only that Loom has a
   * doubt; what the doubt IS comes from here.
   */
  readonly text: string;
}

/**
 * The comprehension objection to show on the ready screen, or null.
 *
 * Advisory only. Nothing here touches `allGreen`, the accept token, or the
 * Accept button — the caller renders it as a note, and a card carrying an
 * objection is exactly as applyable as one without.
 */
export const comprehensionObjection = (
  card: ReadinessEntry,
): FocusComprehensionObjection | null => {
  // No model was wired (local dev) → the verdict list is empty anyway, but be
  // explicit: a skipped methodology has no opinion, not a silent pass.
  if (card.methodologySkipped) return null;
  const hit = selectComprehensionObjection(card.invest.verdicts);
  if (!hit) return null;
  // `suggestion` is the correction the judge proposes and is what we want to
  // show; `evidence` is its reading of what is wrong. Either is the model's own
  // prose. If both are blank there is nothing verbatim to render and we render
  // nothing — a bare "something is off" frame with no content would be copy
  // invented off the criterion id, which this criterion has been measured to
  // not support.
  const text = (hit.suggestion ?? "").trim() || hit.evidence.trim();
  if (text === "") return null;
  return { criterion: hit.criterion, level: hit.level, text };
};

// ───────────────────────────────────────────────────────────────────────────
// Narrative position (C2).
//
// `ends` on issue #78 was rendered on the Keep screen as a bare value — the
// user's own true sentence about their problem, stripped of the `so that …`
// position that made it wrong. Showing the POSITION is what makes the defect
// legible in eight words instead of forty-five.
//
// This is the field's narrative role read straight off the shared spec (the
// same `narrative` role the server's own composer keys on), NOT a hardcoded
// field list: a spec that renames or reorders fields moves this with it.
// ───────────────────────────────────────────────────────────────────────────

const NARRATIVE_ROLE_BY_FIELD: ReadonlyMap<string, string> = new Map(
  DEFAULT_READINESS_SPEC.fields.map((f) => [f.name, f.narrative]),
);

export type StoryClauseRole = "role" | "means" | "ends";

/** A clause ready to drop into the review screen's story frame, plus whether
 *  that frame should still contribute its own connective ahead of it. */
export interface StoryClauseSplice {
  readonly text: string;
  readonly connective: boolean;
}

/** The story-sentence role a field fills (`role` / `means` / `ends`), or null
 *  for fields that are not part of the one-line story (sections + metadata). */
export const storyClauseRoleOf = (field: string): StoryClauseRole | null => {
  const r = NARRATIVE_ROLE_BY_FIELD.get(field);
  return r === "role" || r === "means" || r === "ends" ? r : null;
};

/**
 * A clause value as it reads AFTER the story connective, so a value that
 * already carries one ("As a finance manager", "so that I stop losing my
 * place") does not double against the frame the review screen puts in front of
 * it.
 *
 * The strip is the SHARED locale template's own `stripRole` / `stripMeans` /
 * `stripEnds` — the same functions the server's composer calls — not a browser
 * reimplementation of them. Nothing else about the composition is reproduced
 * here: no section splicing, no `normalizeNarrativeClause` cosmetics, no
 * sentence assembly. The composed sentence itself is a server artifact and this
 * is not it.
 *
 * Known limit: `locale` is the operator's UI locale, while the artifact is
 * composed at the CARD's resolved frame locale — the configured project locale
 * when one is set, otherwise whichever language the three story clauses are
 * actually in (`resolveContentLocale`). So the two now differ for an
 * English-UI operator on a German card, not only under a config/UI skew: the
 * user reads "so that …" where the server writes "damit …". Still cosmetic —
 * this frames a value already on screen and is not a claim about the write —
 * but no longer rare. The honest fix is to put the server's resolved frame on
 * the wire and read it here; nothing does yet.
 * Falls back to the untouched value whenever the strip empties it.
 */
export const storyClauseValue = (
  role: StoryClauseRole,
  value: string,
  locale: Locale,
): StoryClauseSplice => {
  const tpl = NARRATIVE_TEMPLATES[locale];
  const stripped = (
    role === "role"
      ? tpl.stripRole(value)
      : role === "means"
        ? tpl.stripMeans(value)
        : tpl.stripEnds(value)
  ).trim();
  const base = stripped === "" ? value.trim() : stripped;
  // The template strip only knows its OWN connective ("so that"), so a value
  // opening on a bare "so" survived it and the frame stacked on top — live, an
  // all-English session read "… so that so we are ready to integrate …".
  // `spliceNarrativeClause` knows both content locales' connectives and, for a
  // connective of a class this frame cannot precede ("to …", "um … zu …"), says
  // so via `connective:false` — which selects the frame that stays silent.
  // NO `lowercaseLead`, deliberately, even though the write path passes it.
  // This surface exists for the user to check THEIR OWN sentence before it is
  // sent; down-casing the first letter of what they typed is a change to their
  // words, and a review screen that quietly edits them is worse than one that
  // differs from the artifact by a capital. The connective and seam-punctuation
  // repairs below ARE shared, because those fix a doubling the user can see and
  // did not write.
  const splice = spliceNarrativeClause(role, base);
  return {
    text: splice.text === "" ? base : splice.text,
    connective: splice.connective,
  };
};
